import * as Sentry from "@sentry/react";

// Browser page translation (Chrome/Google Translate, Yandex Browser, Safari)
// replaces text nodes with its own wrapper elements (<font>, <ya-tr-span>).
// React keeps references to the original text nodes, so its next
// `insertBefore(newNode, staleTextNode)` or `removeChild(staleTextNode)`
// throws NotFoundError and the whole route crashes to the error page.
// See https://github.com/facebook/react/issues/11538
//
// The guard patches both methods once. When the reference/child node is no
// longer a child of the parent, insertBefore falls back to appendChild and
// removeChild becomes a no-op. The first fallback per page load is reported.
//
// Trade-off: on a translated page a text node React removed may stay visible
// as the translator's ghost copy, and an inserted node may land at the end of
// its parent instead of its exact position. Both beat a white-screen crash.

type GuardedMethod = "insertBefore" | "removeChild";

let installed = false;
let reported = false;

const reportOnce = (method: GuardedMethod, parent: Node, node: Node) => {
  if (reported) return;
  reported = true;

  const parentName = parent.nodeName.toLowerCase();
  const nodeName = node.nodeName.toLowerCase();
  console.warn(
    `[domTranslationGuard] ${method}: <${nodeName}> is no longer a child of <${parentName}>. ` +
      "The page was probably rewritten by browser translation; falling back so the app keeps running.",
  );
  Sentry.captureMessage(`domTranslationGuard: stale ${method} reference`, {
    level: "warning",
    tags: { method },
    extra: { parent: parentName, node: nodeName },
  });
};

export const installDomTranslationGuard = () => {
  if (installed) return;
  installed = true;

  const { insertBefore, removeChild } = Node.prototype;

  Node.prototype.insertBefore = function guardedInsertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      reportOnce("insertBefore", this, referenceNode);
      return this.appendChild(newNode);
    }
    return insertBefore.call(this, newNode, referenceNode) as T;
  };

  Node.prototype.removeChild = function guardedRemoveChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child && child.parentNode !== this) {
      reportOnce("removeChild", this, child);
      return child;
    }
    return removeChild.call(this, child) as T;
  };
};
